import { ChangeEvent, useEffect } from 'react';
import { memo, useRef, useState } from 'react';
import { useFormikContext, useField } from 'formik';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { useLocale } from '../../../../context/Localization/localization';
import { WebFormTagNumberFieldProps } from '../FormTagNumberField.types';
import { TagNumberPatternMask } from '../TagNumberPatternMask/TagNumberPatternMask';
import { TextFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';

export const WebFormTagNumberField = memo(
    ({
        name,
        id,
        title,
        label,
        ariaLabel,
        disabled,
        required,
        autoFocus,
        capsLock,
        fullWidth,
        seventyFivePercentWidth,
        tabFocusRefContext,
        onChangeCallback,
    }: WebFormTagNumberFieldProps) => {
        /**
         * Not all formik validation accounts for TextFields that are null.
         * So even though we pass an undefined validation type to the function,
         * it is still required in order to have null required fields validate.
         */
        const [field, meta] = useField<string>({
            name,
            validate: TextFieldValidation({
                required: required ?? false,
                label,
                name,
                validationType: undefined,
            }),
        });
        const { submitCount, setFieldValue } = useFormikContext();

        /**
         * A ref to the HTMLInputElement in the MUI TextField.
         */
        const inputRef = useRef<HTMLInputElement>(null);
        /**
         * Retains the internal value as a state to force a render when it changes.
         *
         * This may not be needed.
         */
        const [value, setValue] = useState<string>(field.value ?? '');
        useScrollToOnError({
            scrollable: inputRef,
            name: name,
        });

        const locale = useLocale();
        const {
            dealer: { primaryLanguage },
        } = useKioskState();

        useEffect(() => {
            if (tabFocusRefContext) {
                tabFocusRefContext.current = inputRef.current;
            }
        }, [tabFocusRefContext]);

        useEffect(() => {
            /**
             * When formik value is different than our internal value, then we go ahead and synchronize.
             *
             * This may not be needed.
             */
            if (field.value !== value) {
                setValue(field.value);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [field.value]);

        const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            onChangeCallback && onChangeCallback();
            setValue(event.target.value);
            setFieldValue(name, event.target.value);
        };

        return (
            <BaseTextField
                name={name}
                id={id}
                title={title}
                label={label}
                ariaLabel={ariaLabel}
                inputMode={'text'}
                type={'text'}
                inputRef={inputRef}
                value={value}
                error={meta.error}
                touched={meta.touched || submitCount > 0}
                disabled={disabled}
                required={required}
                autoFocus={autoFocus}
                fullWidth={fullWidth}
                seventyFivePercentWidth={seventyFivePercentWidth}
                capsLock={capsLock}
                onChange={handleOnChange}
                InputProps={{
                    inputComponent: TagNumberPatternMask as any,
                }}
                inputProps={{
                    maxLength: 4,
                }}
                safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
            />
        );
    },
);
